import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { colors } from '@tempmee/tailwind';

import { remoteConfigReady } from '~/api/firebaseConfig';
import { SaltclickChatbot } from '~/constants/ff.posthog';
import {
  buildSaltclickChatIdentity,
  clearSaltclickSession,
  closeOpenSaltclickConversations,
  endSaltclickChatSession,
  fetchSaltclickIdentityToken,
  getSaltclickVerifiedForUserId,
  installSaltclickEndSignals,
  purgeStoredSaltclickConversation,
  ISaltclickChatIdentity,
  ISaltclickSessionStatusPayload,
  setSaltclickVerifiedForUserId,
  toSaltclickPrechatFields,
} from '~/domain/SaltclickChat';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';
import { routes } from '~/routes';
import { TRootState } from '~/store/types';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

const SCRIPT_ID = 'saltclick-bootstrap';
// Salesforce can fail silently (blocked script, CORS on its own subrequests),
// so the skeleton gets a deadline instead of pulsing forever.
const SKELETON_TIMEOUT_MS = 20000;
/**
 * How long after the ready event to check that a launcher actually painted. Ready
 * fires before the widget renders, so an immediate check would always fail.
 */
const PAINT_CHECK_DELAY_MS = 5000;
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';
const READY_CLASS = 'saltclick-ready';
/** Set while a conversation is being retired — see `RETIRING_UNBLOCK_MS`. */
const RETIRING_CLASS = 'saltclick-retiring';
/**
 * Set while the widget is showing a conversation that has been retired — see
 * `STALE_VIEW_GUARD_MS`.
 */
const GUARDED_CLASS = 'saltclick-guarded';
/**
 * How long the widget may be inert while it replaces a retired conversation.
 *
 * The window this covers: the first time the chat window is opened after a chat was
 * retired, it comes back showing the view the ended conversation left behind — including
 * a **New Conversation** button, which does what it always does and starts a new *session*
 * on the conversation the widget is still holding. That conversation was deleted, so the
 * button reconnects forever and takes the widget's UI with it, unrecoverable without a
 * reload. It cannot be disabled from here: it is rendered on Salesforce's origin, so the
 * only reach this page has is the frame it sits in.
 *
 * The widget opens a fresh conversation on its own within a second or two, and that is
 * what replaces the stale view — so this is the gap between opening and that arriving,
 * not a feature. A deadline rather than a promise, for the same reason as
 * `RETIRING_UNBLOCK_MS`: a widget nobody hands back is worse than a button nobody should
 * press.
 */
const STALE_VIEW_GUARD_MS = 8000;
/**
 * How long the launcher may stay a placeholder before the widget is handed back
 * regardless.
 *
 * Retiring a conversation is several round trips to two hosts, and a launcher that never
 * comes back is worse than any of the churn this replaces — `hideChatButton` was dropped
 * from this flow for exactly that reason. So it is a deadline, not a promise about the
 * flow finishing, and it is why hiding the widget here is safe when hiding it by API call
 * was not.
 */
const RETIRING_UNBLOCK_MS = 8000;

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 *
 * The `::after` skeleton stands in for the launcher whenever there is no launcher
 * worth clicking — before the widget paints, and again while a conversation is
 * being retired. Both are the same state from where the user sits: something is
 * happening and the corner is not ready yet, so it says so in the launcher's own
 * place and size rather than leaving a button that ignores clicks. ponytail: a
 * pseudo-element keeps this out of the React tree; promote it to a real component
 * if it ever needs interaction.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* Out of the way while its conversation is retired, and the placeholder below takes
       its place. Not polish: the flow lists every open conversation and closes what it
       finds, so a user who reopens the launcher and starts a new chat mid-flight would
       have that new conversation closed under them. It also spares them the churn —
       reopening a window that \`clearSession\` is about to tear down and rebuild is what
       makes the widget blink repeatedly. \`RETIRING_CLASS\` is removed in a \`finally\`
       and on a deadline, so neither the hiding nor the placeholder can outlast the flow. */
    body:not(.${VISIBLE_CLASS}) #embedded-messaging,
    body.${RETIRING_CLASS} #embedded-messaging { display: none !important; }
    /* Belt and braces on the same thing, for a deployment whose own styles win the
       display fight: a widget that is somehow still painted still takes no clicks. */
    body.${RETIRING_CLASS} #embedded-messaging { pointer-events: none !important; }
    /* Visible but inert, which is the difference from the rule above. The widget is
       showing a retired conversation's leftover view and is about to replace it with a
       live one; until it does, its **New Conversation** button would start a session on
       the conversation we deleted. The button cannot be reached from this origin, so the
       frame it sits in is what goes inert — the user sees the widget connecting, which is
       what is actually happening, and cannot press the one thing that breaks it. */
    body.${GUARDED_CLASS} #embedded-messaging { pointer-events: none !important; }
    body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after,
    body.${VISIBLE_CLASS}.${RETIRING_CLASS}::after {
      content: '';
      position: fixed;
      bottom: 1.25rem;
      right: 1.25rem;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 9999px;
      background: ${colors.neutral[200]};
      animation: saltclick-skeleton-pulse 1.5s ease-in-out infinite;
    }
    @keyframes saltclick-skeleton-pulse { 50% { opacity: 0.4; } }
    @media (prefers-reduced-motion: reduce) {
      body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after,
      body.${VISIBLE_CLASS}.${RETIRING_CLASS}::after { animation: none; }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot. Called once
 * from the authenticated layout so the widget boots in the background right
 * after login and the launcher is already painted (just hidden) by the time
 * the user opens Help — no load-then-pop. Gated by the
 * `GOTU-4448-saltclick-chatbot` PostHog flag. GOTU-5243.
 *
 * The session is verified rather than anonymous: auth-service mints a short-lived
 * JWT for the logged-in account and the widget is told to trust it, which is what
 * separates one user's conversation from the next in a shared tab. GOTU-5918.
 *
 * And a chat the user ends stays ended: Salesforce's own End chat ends the messaging
 * session but leaves the conversation open, and the widget resumes any open conversation
 * as it boots — so the next chat continued the last one. `handleChatEnded` retires it
 * over the MIAW REST API, which is the one thing here no client API can do.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  // The office being acted as — under an office switch, the selected child.
  // This is what pre-chat reports, since it is who the chat is about.
  const user = useUserSelector();
  const officeId: string | undefined = user?.id;
  // The account the access token belongs to, which is who auth-service mints for
  // and therefore what a verified session is keyed on. Same as `officeId` unless
  // a parent is acting as one of its child offices.
  const accountId = useSelector((state: TRootState) => state.user.user?.id);
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;
  const isHelpRoute = useLocation().pathname === routes.help;

  const [isMessagingReady, setIsMessagingReady] = useState(false);

  // Read from event handlers (ready, token expiry) that outlive the render they
  // were registered in, so they need the current user, not a captured one.
  const identityRef = useRef<ISaltclickChatIdentity | null>(null);
  const accountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    identityRef.current = buildSaltclickChatIdentity(user);
    accountIdRef.current = accountId;
  }, [accountId, user]);

  /**
   * Stages the hidden pre-chat fields for the NEXT conversation.
   *
   * Salesforce takes these only after the ready event and before a conversation
   * begins, and a late write is dropped in silence — no error, no clue. So every
   * conversation needs its own, which is why this is callable rather than only an
   * effect: after a chat ends, the next conversation has not begun, and that is the
   * one window left to stage it in. Without that write the next conversation starts
   * with nothing staged and stops on the pre-chat step.
   *
   * `gtAdminPanelId` is the field Salesforce keys session control off; `userType`
   * tells an agent which kind of account they are talking to.
   */
  const sendPrechatFields = useCallback((): void => {
    const identity = identityRef.current;
    if (!identity) return;

    const prechatApi = window.embeddedservice_bootstrap?.prechatAPI;
    if (!prechatApi) {
      // Before ready the widget simply does not exist yet, and `isMessagingReady`
      // brings the effect below back — so only a *ready* widget without `prechatAPI`
      // is an anomaly. Reported for the same reason as the missing
      // `userVerificationAPI` above: the conversation would otherwise reach an agent
      // with no office on it and nothing would say why.
      if (window.embeddedservice_bootstrap) {
        console.error(
          '[Saltclick] Widget is ready but exposes no prechatAPI — no pre-chat ' +
            'form is configured on this Salesforce deployment, so the conversation ' +
            'carries no gtAdminPanelId',
        );
      }

      return;
    }

    const fields = toSaltclickPrechatFields(identity);
    prechatApi.setHiddenPrechatFields(fields);
    // Field *names* only: the identity holds PII (`email`) that this must never
    // put in a console. Which keys went is the useful part anyway — Salesforce
    // discards undeclared fields silently, so `DECLARED_PRECHAT_FIELDS` is what
    // this line lets you confirm against their config.
    console.log(
      `[Saltclick] Sent hidden pre-chat fields for ${identity.gtAdminPanelId}: ` +
        `${Object.keys(fields).join(', ')}`,
    );
  }, []);

  /** The account a token request is currently out for, if any. */
  const requestedForRef = useRef<string | null>(null);
  /**
   * Fetches a token for whoever is logged in now and hands it to the widget.
   *
   * Every trigger — first ready, a fresh login, token expiry, a closed
   * conversation — re-calls the endpoint rather than reusing a held token. A token
   * is short-lived (900s) and bound to the conversation it opened, so a cached one
   * is the wrong thing to re-hand; and keeping none means each trigger is
   * self-contained, instead of the outcome depending on which of two paths reached
   * the shared token first.
   *
   * The marker is the only thing that makes this cheap to call: already verified as
   * this account means there is nothing to do, so the repeat triggers cost nothing.
   */
  const verifySession = useCallback(async (): Promise<void> => {
    const account = accountIdRef.current;
    if (!account) return;

    // Nothing to hand a token to yet. `userVerificationAPI` appears with the
    // widget, and the ready event re-runs this — so this is the normal early path,
    // not a failure (`onReady` reports a ready widget that still lacks it).
    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (!verificationApi) return;

    if (getSaltclickVerifiedForUserId() === account) return;

    // One request in flight per account: this runs from the mount, from the ready
    // event, from token expiry and from a closed conversation, which overlap.
    if (requestedForRef.current === account) return;
    requestedForRef.current = account;

    try {
      // No argument: auth-service mints for whoever the access token belongs to,
      // so the token's subject is the answer, not something we ask for.
      const identityToken = await fetchSaltclickIdentityToken();

      // A token that arrives after the account changed belongs to nobody we can
      // use it for, so it is dropped rather than handed over.
      if (accountIdRef.current !== account) return;

      // Belt and braces on the one thing that must not go wrong: the token has
      // to be for the account we think is logged in. Anything else would file
      // the conversation under someone else.
      if (identityToken.sub && identityToken.sub !== account) {
        console.error(
          `[Saltclick] identity token was minted for ${identityToken.sub}, ` +
            `not ${account} — leaving the chat session anonymous`,
        );

        return;
      }

      // Another trigger verified this account while the request was out.
      if (getSaltclickVerifiedForUserId() === account) return;

      verificationApi.setIdentityToken({
        identityTokenType: identityToken.tokenType ?? 'JWT',
        identityToken: identityToken.token,
      });
      setSaltclickVerifiedForUserId(account);
      // Logged because the failure this replaced was silent: an anonymous session
      // looks identical to a verified one from the outside, so "did the token
      // land?" was otherwise unanswerable from a support session. The token itself
      // is never logged — it is a bearer credential; `kid` and `expiresIn` identify
      // it without carrying anything sensitive.
      console.log(
        `[Saltclick] Verified chat session for ${account} (kid ${identityToken.kid ?? 'unknown'}` +
          `, expires in ${identityToken.expiresIn ?? '?'}s)`,
      );

      // Every re-verify follows a `clearSession`, and `clearSession` rebuilds the widget's
      // markup — which loses the staged pre-chat fields with it. Staging here covers all
      // three paths at once (an account handover, a token the widget says expired, and the
      // end-of-chat flow) instead of each remembering separately; two of them used to
      // forget, and a conversation that begins with no `gtAdminPanelId` has nothing on it
      // for Salesforce to key session control off.
      sendPrechatFields();
    } catch (err) {
      // Leaves the session anonymous rather than taking the whole widget down.
      // Includes the 503 an environment returns until its signing key is
      // provisioned, and the 400 for an account whose user type Salesforce has no
      // mapping for. Note this deployment runs `authMode: "Auth"`, where an
      // anonymous session cannot start a conversation at all — the launcher is
      // there but inert, so this line is the only explanation of why.
      console.error('[Saltclick] Could not mint a chat identity token: ', err);
    } finally {
      // Only clear the slot this call owns; a newer request for a different
      // account has already claimed it.
      if (requestedForRef.current === account) requestedForRef.current = null;
    }
  }, [sendPrechatFields]);

  /**
   * One treatment per ended conversation, whichever signal reaches it first.
   *
   * `isHandling` is the in-flight guard. `hasHandled` is released only when a new
   * conversation is in play, because the signals do not arrive once each: End chat
   * produces an RPC method and usually a session-status event, and the `clearSession`
   * below resets the widget, which is itself an end signal.
   */
  const endStateRef = useRef({ isHandling: false, hasHandled: false });

  /**
   * The conversation this document retired and has not seen replaced yet, and the timer
   * holding the widget inert until it is. Refs because the listeners that read them
   * outlive the render they were registered in.
   */
  const retiredConversationRef = useRef<string | undefined>(undefined);
  const staleViewTimerRef = useRef<number | undefined>(undefined);

  /**
   * Retires the conversation the user just ended, so the next chat is a new one.
   *
   * The order is the whole design, and each step cost a round of testing:
   *
   *  1. close the conversation over scrt2 — `clearSession` cannot, it only reaches the
   *     `/session` variant, and the widget resumes any open conversation as it boots;
   *  2. `clearSession(false)` — closing server-side says nothing about what the widget
   *     still believes it holds, and there is no API for handing it that news;
   *  3. re-verify — the widget asks for a token the moment it is cleared, and the next
   *     chat cannot start on this `authMode: "Auth"` deployment without one;
   *  4. re-stage the pre-chat fields — the next conversation has not begun, which is the
   *     only window Salesforce accepts them in.
   */
  const handleChatEnded = useCallback(
    async (trigger: string, knownConversationIds: string[] = []): Promise<void> => {
      const account = accountIdRef.current;
      if (!account) return;

      // Only an end for the session this document holds. Logout ends the session too, and
      // the same clear empties the widget's storage — which the storage watcher reads as
      // an end moments later. Without this the last act of a logout would be to retire the
      // conversation the user is expected to come back to.
      if (getSaltclickVerifiedForUserId() !== account) {
        console.log(
          `[Saltclick] [${trigger}] a chat ended, but this document holds no verified ` +
            'session — leaving the conversation alone',
        );

        return;
      }

      const { orgId, deploymentName, scrt2Url } = RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !scrt2Url) {
        console.error(
          `[Saltclick] [${trigger}] a chat ended, but SALTCLICK_CHAT_CONFIG is ` +
            'incomplete — the conversation stays open and the next chat will resume it',
        );

        return;
      }

      const endState = endStateRef.current;
      if (endState.isHandling || endState.hasHandled) {
        // Not a warning: a second signal for the same end is normal, and saying so is how
        // you tell "already handled" apart from "never fired".
        console.log(`[Saltclick] [${trigger}] already handled for this end — not running twice`);

        return;
      }
      endState.isHandling = true;
      endState.hasHandled = true;
      console.log(`[Saltclick] [${trigger}] chat ended — retiring the conversation`);

      // Nothing reaches the widget until this settles. The deadline is what makes that
      // safe to say: an inert launcher nobody hands back is worse than the churn it is
      // here to prevent.
      document.body.classList.add(RETIRING_CLASS);
      const unblockTimer = window.setTimeout(() => {
        console.error(
          `[Saltclick] Retiring the conversation took longer than ${RETIRING_UNBLOCK_MS}ms — ` +
            'putting the launcher back before it finishes',
        );
        document.body.classList.remove(RETIRING_CLASS);
      }, RETIRING_UNBLOCK_MS);

      // Put an open window away before anything else. Probed rather than assumed — this
      // deployment runs the WebV2 client, whose surface is not the documented one — and
      // reported when absent, because a window left open is what makes the widget relaunch
      // itself after a clear, and that relaunch is the blink a user sees.
      const utilApi = window.embeddedservice_bootstrap?.utilAPI;
      try {
        if (utilApi?.minimizeChat) utilApi.minimizeChat();
        else
          console.error(
            '[Saltclick] No utilAPI.minimizeChat on this deployment — an open chat window will be torn down in place',
          );
      } catch (err) {
        console.error('[Saltclick] minimizeChat threw: ', err);
      }

      let retired = false;
      try {
        await closeOpenSaltclickConversations({
          scrt2Url,
          orgId,
          deploymentName,
          knownConversationIds,
        });
        retired = true;
        // What the widget will come back showing, and what its New Conversation button
        // would try to start a session on. Remembered so the first open after this can be
        // held inert until a live conversation replaces the view.
        retiredConversationRef.current = knownConversationIds[0] ?? 'unknown';

        // Half of making the widget let go, and the half `clearSession` does not do: it
        // decides what to resume from what it kept in web storage on this origin, and it
        // looks there before anything on this page can intervene. An id left behind now
        // points at a conversation that was deleted a moment ago, which is what had the
        // widget open, sit on its hello screen and shut itself on every click until the
        // page was reloaded. Safe here and nowhere else: everything the account had open
        // has just been closed, so an id still in storage is dead by definition.
        purgeStoredSaltclickConversation();

        // The other half, and never conditionally — this is the step that lets the user
        // have another chat at all.
        //
        // It reads like it could be skipped, and it cannot. After its own End chat the
        // widget removes its conversation id from web storage, which looks exactly like
        // letting go, and it has not: the conversation is still in the client's memory,
        // and `New Conversation` starts a new SESSION on the conversation it is holding.
        // With that conversation deleted there is nothing to start on, so the widget sits
        // on its hello screen — through a collapse, through another click, until the
        // document is replaced. Measured on dev, and the same wall the first attempt at
        // this ticket hit.
        //
        // `false`, because the conversation is gone: having the widget call its own
        // `endSession` would be a 403 on something that no longer exists.
        //
        // The cost is that `clearSession` tears the widget's markup down and rebuilds it,
        // which the user sees. That is what being minimized first and unclickable
        // throughout is for — it cannot be avoided, only kept out of the way.
        await clearSaltclickSession(false);
        // Dropping the marker is what lets `verifySession` past its already-verified
        // guard, and is true in its own right — the widget is verified as nobody now.
        setSaltclickVerifiedForUserId(undefined);
        // The token has to be back before the launcher answers again: this deployment
        // runs `authMode: "Auth"`, where an unverified widget cannot open a conversation.
        // Inside the block, therefore, not after it.
        await verifySession();
        console.log('[Saltclick] Ready for a new conversation');
      } catch (err) {
        console.error('[Saltclick] Could not retire the ended conversation: ', err);
        // Released only when nothing was taken down yet, so the next signal for this same
        // end gets a turn. Past the close there is no retry: the clear that follows it is
        // itself an end signal, so releasing the guard there would spin.
        if (!retired) endState.hasHandled = false;
      } finally {
        endState.isHandling = false;
        window.clearTimeout(unblockTimer);
        document.body.classList.remove(RETIRING_CLASS);
      }
    },
    // `verifySession` re-stages the pre-chat fields itself, which is why they are not
    // written here as well.
    [verifySession],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    ensureVisibilityStyle();
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    // A conversation being in play is the only thing that makes the next end meaningful,
    // and so the only thing that releases the guard. Both events, because either can be
    // first: a resumed conversation opens without starting.
    const onConversationInPlay = () => {
      endStateRef.current.hasHandled = false;
    };
    window.addEventListener('onEmbeddedMessagingConversationStarted', onConversationInPlay);
    window.addEventListener('onEmbeddedMessagingConversationOpened', onConversationInPlay);

    /**
     * The only public event that reports a chat ending — and only sometimes.
     *
     * `Ended` does not mean the user ended it: Agentforce ends an idle session within
     * about a minute, and typing after that starts a new session on the same conversation
     * with its history intact. Acting on those took chats away from people who were still
     * reading them, so `sessionEndedByRole` decides, and only `EndUser` is intent.
     *
     * Which also leaves the gap the RPC tap exists to fill: a session Agentforce already
     * timed out is already `Ended`, so pressing End chat afterwards changes no status and
     * fires no event at all.
     */
    const onSessionStatusUpdate = (event: Event) => {
      const { detail } = event as CustomEvent<{
        conversationEntry?: { entryPayload?: string };
        conversationId?: string;
      }>;

      let payload: ISaltclickSessionStatusPayload;
      try {
        // A JSON string inside the detail, not an object. Parsed defensively: a shape
        // change should cost this trigger, not the widget.
        payload = JSON.parse(
          detail?.conversationEntry?.entryPayload ?? '{}',
        ) as ISaltclickSessionStatusPayload;
      } catch (err) {
        console.error('[Saltclick] A session status update carried an unreadable payload: ', err);

        return;
      }

      const conversationId = payload.conversationIdentifier ?? detail?.conversationId;
      // Logged before any guard runs, because this event is the only public word on a chat
      // ending: a path that returns in silence is indistinguishable from Salesforce having
      // sent nothing, and those are very different problems.
      console.log(
        `[Saltclick] Session status ${payload.sessionStatus ?? '?'} (was ` +
          `${payload.sessionStatusPrev ?? '?'}) by ${payload.sessionEndedByRole ?? 'nobody named'}` +
          ` on conversation ${conversationId ?? '?'}`,
      );

      // A conversation that is not the one we retired is the live one taking its place,
      // which is exactly what the guard was waiting for.
      if (conversationId && conversationId !== retiredConversationRef.current) {
        retiredConversationRef.current = undefined;
        releaseStaleViewGuard(`conversation ${conversationId} is in play`);
      }

      if (payload.sessionStatus !== 'Ended') return;

      if (payload.sessionEndedByRole !== 'EndUser') {
        console.log(
          '[Saltclick] Not the user ending the chat — the conversation stays open to ' +
            'carry on in',
        );

        return;
      }

      void handleChatEnded(
        'onEmbeddedMessagingSessionStatusUpdate',
        conversationId ? [conversationId] : [],
      );
    };
    window.addEventListener('onEmbeddedMessagingSessionStatusUpdate', onSessionStatusUpdate);

    /**
     * The last moment a conversation can be given its office.
     *
     * Salesforce takes hidden pre-chat fields after ready and before a conversation
     * begins, and drops a late write in silence — no error, no clue. So they are staged
     * again the moment the user reaches for the launcher, which is the closest this page
     * can get to "just before a conversation starts" without knowing that a conversation
     * is starting.
     *
     * Both events, and neither assumed to fire: most of this deployment's documented
     * events never arrive, and the cost of a write nobody needed is a discarded call.
     * That is the trade this makes — the alternative is a conversation reaching an agent
     * with no `gtAdminPanelId` on it, and nothing anywhere saying why.
     */
    const onLauncherReached = () => sendPrechatFields();
    window.addEventListener('onEmbeddedMessagingButtonClicked', onLauncherReached);
    window.addEventListener('onEmbeddedMessagingWindowMaximized', onLauncherReached);

    /** Hands the widget back, however the wait ended. */
    const releaseStaleViewGuard = (reason: string) => {
      if (!staleViewTimerRef.current) return;

      window.clearTimeout(staleViewTimerRef.current);
      staleViewTimerRef.current = undefined;
      document.body.classList.remove(GUARDED_CLASS);
      console.log(`[Saltclick] The widget is live again — ${reason}`);
    };

    /**
     * Holds the widget inert the first time it is opened after a chat was retired.
     *
     * It comes back showing what the retired conversation left behind, and that view has a
     * **New Conversation** button which starts a session on the conversation the widget is
     * still holding — the one we deleted. Pressing it reconnects forever and takes the
     * widget with it until the page is reloaded. There is no way to disable a button on
     * Salesforce's origin, so the frame goes inert until the widget has replaced the view
     * with a live conversation, which it does on its own within a second or two.
     *
     * Nothing is lost by waiting: opening the window is what starts the next conversation,
     * and that happens without a click.
     */
    const onWindowOpened = () => {
      if (!retiredConversationRef.current || staleViewTimerRef.current) return;

      console.log(
        `[Saltclick] The widget is showing the view retired conversation ` +
          `${retiredConversationRef.current} left behind — holding it inert until a live ` +
          'conversation replaces it',
      );
      document.body.classList.add(GUARDED_CLASS);
      staleViewTimerRef.current = window.setTimeout(() => {
        console.error(
          `[Saltclick] No new conversation within ${STALE_VIEW_GUARD_MS}ms — handing the ` +
            'widget back with the retired view still showing',
        );
        // Deliberately not cleared: the view is still the stale one, so the next open
        // should guard again.
        releaseStaleViewGuard('the wait timed out');
      }, STALE_VIEW_GUARD_MS);
    };
    window.addEventListener('onEmbeddedMessagingWindowMaximized', onWindowOpened);
    window.addEventListener('onEmbeddedMessagingConversationOpened', onWindowOpened);

    // Two names that read like the answer and are not: `onEmbeddedMessagingEndSession` is
    // an internal iframe event that never reaches the host, and
    // `onEmbeddedMessagingConversationClosed` has never been seen to fire on this
    // deployment. Registered anyway — they cost two listeners, they are the documented
    // way this is supposed to work, and the guard makes an extra signal free.
    const onEndSession = () => {
      void handleChatEnded('onEmbeddedMessagingEndSession');
    };
    window.addEventListener('onEmbeddedMessagingEndSession', onEndSession);
    const onConversationClosed = () => {
      // The next conversation has not begun, which is the window Salesforce accepts these
      // in. Belt and braces for the case where the end below cannot run.
      sendPrechatFields();
      void handleChatEnded('onEmbeddedMessagingConversationClosed');
    };
    window.addEventListener('onEmbeddedMessagingConversationClosed', onConversationClosed);

    // Before the bootstrap script is appended below, and there is no second chance at it:
    // the RPC tap has to be in place before the widget builds its channel.
    const removeEndSignals = installSaltclickEndSignals({
      onChatEnded: (trigger) => {
        void handleChatEnded(trigger);
      },
      // The second way out of the one-per-end guard. `onEmbeddedMessagingConversationStarted`
      // is the first, and it is a Salesforce event on a deployment where most of them never
      // arrive — if it is one of those, the guard latches after the first end and nothing
      // ends again for the rest of the page load.
      onConversationSeen: onConversationInPlay,
    });

    // The class swaps the skeleton for the real launcher (or drops it when the
    // widget never arrives). It lives on the body, not in state, so it survives
    // the hook remounting on a fresh login.
    const hideSkeleton = () => document.body.classList.add(READY_CLASS);

    /**
     * What is known about the widget right now, for the lines below.
     *
     * Each of these is a different reason for an empty corner, and they are
     * indistinguishable from the outside: the script never loaded, it loaded but
     * `init` was never reached, `init` ran but the widget never said it was ready, or
     * everything worked and our own CSS is hiding it.
     */
    const widgetState = () =>
      [
        `script ${document.getElementById(SCRIPT_ID) ? 'present' : 'missing'}`,
        `bootstrap ${window.embeddedservice_bootstrap ? 'present' : 'missing'}`,
        `container ${document.getElementById('embedded-messaging') ? 'present' : 'missing'}`,
        `body classes ${document.body.className || '(none)'}`,
      ].join(', ');

    /**
     * Reports a widget that never rendered anything.
     *
     * Ready is not the same as visible: the event says the widget booted, not that it
     * painted a launcher. A ready widget with an empty container is the "chat is just
     * not there" report, and until now it produced no log of any kind — the skeleton
     * timed out, the corner went empty, and nothing said why.
     */
    let paintCheckTimer: number | undefined;
    const checkWidgetPainted = () => {
      const container = document.getElementById('embedded-messaging');
      if (container?.childElementCount) return;

      console.error(`[Saltclick] Widget reported ready but rendered nothing — ${widgetState()}`);
    };

    const onReady = () => {
      // Or the deadline below fires twenty seconds into every session and reports a widget
      // that has been ready the whole time — which is worse than no diagnostic, because it
      // is the line someone reads while looking for a real failure.
      window.clearTimeout(skeletonTimer);
      hideSkeleton();
      setIsMessagingReady(true);
      console.log('[Saltclick] Widget ready');
      // Late enough for the launcher to have painted, since ready fires before it.
      paintCheckTimer = window.setTimeout(checkWidgetPainted, PAINT_CHECK_DELAY_MS);
      // `userVerificationAPI` only exists when User Verification is enabled on the
      // Salesforce deployment. Without it there is nothing to hand a token to, so
      // say so once — otherwise the session is quietly anonymous and the missing
      // token request looks like a bug on this side.
      if (!window.embeddedservice_bootstrap?.userVerificationAPI) {
        console.error(
          '[Saltclick] Widget is ready but exposes no userVerificationAPI — User ' +
            'Verification is not enabled on this Salesforce deployment, so the chat ' +
            'session stays anonymous',
        );
      }
    };
    window.addEventListener('onEmbeddedMessagingReady', onReady);
    // The deadline used to drop the skeleton in silence, which is the worst version of
    // this failure: the corner simply goes empty, with nothing in the console to say
    // the widget never arrived or how far it got.
    const skeletonTimer = window.setTimeout(() => {
      hideSkeleton();
      console.error(
        `[Saltclick] Widget never became ready in ${SKELETON_TIMEOUT_MS}ms — ${widgetState()}`,
      );
    }, SKELETON_TIMEOUT_MS);

    // The widget asks for a token whenever the one it holds stops being usable —
    // expiry, or the reset that follows clearSession.
    const onIdentityTokenExpired = () => {
      // Dropping the marker is what lets `verifySession` past its already-verified
      // guard, and is true in its own right: the session stops counting as verified
      // until a fresh token is handed over.
      setSaltclickVerifiedForUserId(undefined);
      void verifySession();
    };
    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);

    let cancelled = false;
    // Remote config is fetched fire-and-forget at startup, so on a first login
    // the values aren't activated yet — wait for them instead of bailing out.
    void remoteConfigReady.then(() => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;

      // Same SALTCLICK_CHAT_CONFIG remote-config key as mobile; fails closed
      // (no bundled default) when unset or incomplete.
      const { orgId, deploymentName, siteUrl, scrt2Url } =
        RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !siteUrl || !scrt2Url) {
        console.error('[Saltclick] Missing or incomplete SALTCLICK_CHAT_CONFIG remote config');
        hideSkeleton();
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onerror = () => {
        console.error('[Saltclick] Failed to load bootstrap script');
        hideSkeleton();
      };
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          console.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          hideSkeleton();
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          // Otherwise "the script loaded but init was never reached" and "init ran and
          // the widget never appeared" look identical from a console.
          console.log(`[Saltclick] Calling init for ${deploymentName} @ ${orgId}`);
          bootstrap.init(orgId, deploymentName, siteUrl, { scrt2URL: scrt2Url });
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
          hideSkeleton();
        }
      };
      document.body.appendChild(script);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(skeletonTimer);
      window.clearTimeout(paintCheckTimer);
      removeEndSignals();
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      window.removeEventListener('onEmbeddedMessagingConversationStarted', onConversationInPlay);
      window.removeEventListener('onEmbeddedMessagingConversationOpened', onConversationInPlay);
      releaseStaleViewGuard('the widget is going away');
      document.body.classList.remove(GUARDED_CLASS);
      window.removeEventListener('onEmbeddedMessagingButtonClicked', onLauncherReached);
      window.removeEventListener('onEmbeddedMessagingWindowMaximized', onLauncherReached);
      window.removeEventListener('onEmbeddedMessagingWindowMaximized', onWindowOpened);
      window.removeEventListener('onEmbeddedMessagingConversationOpened', onWindowOpened);
      window.removeEventListener('onEmbeddedMessagingSessionStatusUpdate', onSessionStatusUpdate);
      window.removeEventListener('onEmbeddedMessagingEndSession', onEndSession);
      window.removeEventListener('onEmbeddedMessagingConversationClosed', onConversationClosed);
      window.removeEventListener('onEmbeddedMessagingReady', onReady);
      window.removeEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);
    };
    // `handleChatEnded` is stable, and it is what every end signal this effect registers
    // ends up in, so it belongs here rather than being silenced.
  }, [enabled, handleChatEnded, sendPrechatFields, verifySession]);

  // Verified identity. `userVerificationAPI` only exists once messaging is ready,
  // so it is the real gate — `isMessagingReady` is just the signal to re-run.
  // Keyed on the account rather than the acting office: switching between one's
  // own offices is the same person and must not restart the conversation, while a
  // different login in the same tab must. Also covers a fresh login where the
  // already-init'd widget never fires the ready event again.
  useEffect(() => {
    if (!enabled || !accountId) return;

    if (getSaltclickVerifiedForUserId() === accountId) return;

    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (verificationApi && getSaltclickVerifiedForUserId()) {
      // A session verified as someone else has to be ended before this account
      // can take over — the fallback for a handover logout did not get to clean
      // up after (a crashed tab, or a session dropped without LOGOUT_ACTION).
      void endSaltclickChatSession().then(verifySession);

      return;
    }

    void verifySession();
  }, [accountId, enabled, isMessagingReady, verifySession]);

  // Re-stages when the user changes (office switch, or a fresh login in the same tab)
  // and once the widget is ready to accept them.
  useEffect(() => {
    if (!officeId) return;

    sendPrechatFields();
  }, [isMessagingReady, officeId, sendPrechatFields]);

  useEffect(() => {
    if (!isHelpRoute) return undefined;

    if (!enabled) {
      // Standing on Help with the widget hidden, which is correct on the way out of a
      // session and wrong at any other time. Worth a line because it looks exactly like
      // a broken widget: the launcher is not there and nothing else explains it. The
      // widget is init'd once per document and cannot be re-created, so if `enabled`
      // went false transiently — a user record briefly absent from redux, say — the
      // corner stays empty even though everything loaded.
      console.log(
        '[Saltclick] On Help but the widget is disabled ' +
          `(flag ${flagEnabled ? 'on' : 'off'}, user ${user ? 'present' : 'missing'}) — hiding it`,
      );

      return undefined;
    }

    document.body.classList.add(VISIBLE_CLASS);

    return () => document.body.classList.remove(VISIBLE_CLASS);
  }, [enabled, flagEnabled, isHelpRoute, user]);
};
